import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../AuthContext';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  padding: 0.55rem 1.5rem;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  gap: 1rem;
  min-height: 60px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.55rem 0.85rem;
    flex-wrap: wrap;
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
  flex-shrink: 0;
`;

const BrandIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const BrandName = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const BrandSub = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
`;

const RemoteSwitcher = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-left: 4rem;
  padding-left: 4rem;
  border-left: 1px solid #e5e7eb;

  @media (max-width: ${breakpoints.tablet}) {
    margin-left: 2rem;
    padding-left: 2rem;
  }

  @media (max-width: ${breakpoints.mobile}) {
    order: 2;
    margin-left: auto;
    padding-left: 0;
    border-left: none;
  }
`;

const RemoteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #4b5563;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s ease;
  min-width: 220px;
  max-width: 260px;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }

  & svg {
    flex-shrink: 0;
  }

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0.4rem 0.6rem;
    font-size: 0.76rem;
    min-width: 140px;
    max-width: 160px;
  }
`;

const RemoteDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ empty }) => (empty ? '#9ca3af' : '#2563eb')};
  flex-shrink: 0;
`;

const RemoteCaret = styled.span`
  margin-left: auto;
  color: #9ca3af;
  display: flex;
  align-items: center;
`;

const RemoteName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
`;

const RemoteMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 280px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 0.4rem;
  display: ${({ open }) => (open ? 'block' : 'none')};
  z-index: 60;
  max-height: 380px;
  overflow-y: auto;
`;

const RemoteOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.7rem;
  background: ${({ active }) => (active ? '#eff6ff' : 'transparent')};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 0.85rem;
  color: ${({ active }) => (active ? '#1d4ed8' : '#374151')};
  font-weight: ${({ active }) => (active ? 700 : 500)};

  &:hover {
    background: ${({ active }) => (active ? '#dbeafe' : '#f9fafb')};
  }
`;

const RemoteOptionIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ active }) => (active ? '#dbeafe' : '#f3f4f6')};
  color: ${({ active }) => (active ? '#1d4ed8' : '#6b7280')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & svg {
    width: 14px;
    height: 14px;
  }
`;

const RemoteOptionText = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;
`;

const RemoteOptionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoteOptionType = styled.span`
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 500;
`;

const Check = styled.span`
  color: #2563eb;
  flex-shrink: 0;

  & svg {
    width: 14px;
    height: 14px;
  }
`;

const Tabs = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  flex: 1;
  justify-content: center;
  margin: 0 0.5rem;

  @media (max-width: ${breakpoints.tablet}) {
    gap: 0;
    overflow-x: auto;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    order: 3;
    flex-basis: 100%;
    border-top: 1px solid #f3f4f6;
    padding-top: 0.5rem;
    margin-top: 0.25rem;
  }
`;

const Tab = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.7rem;
  text-decoration: none;
  font-size: 0.88rem;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  color: ${({ active }) => (active ? '#2563eb' : '#4b5563')};
  background: transparent;
  border-radius: 6px;
  white-space: nowrap;
  transition: color 0.12s ease;

  &:hover {
    color: #2563eb;
  }

  & svg {
    width: 16px;
    height: 16px;
    color: ${({ active }) => (active ? '#2563eb' : '#9ca3af')};
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  position: relative;
  background: none;
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #ef4444;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileWrap = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.6rem 0.3rem 0.35rem;
  background: none;
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #111827;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 0.5rem;
  display: ${({ open }) => (open ? 'block' : 'none')};
  z-index: 60;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 0.35rem 0;
`;

const MenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  padding: 0.55rem 0.65rem;
  font-size: 0.85rem;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &:hover {
    background: #f9fafb;
    color: #111827;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: #4b5563;

  &:hover {
    background: #f3f4f6;
  }

  @media (max-width: ${breakpoints.tablet}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const FolderSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" fill="#ffffff" />
  </svg>
);

const FilesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ServerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const UserIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ServerSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const TABS = [
  { path: '/FileView', label: '파일 관리', icon: <FilesIcon /> },
  { path: '/Upload', label: '파일 업로드', icon: <UploadIcon /> },
  { path: '/Servers', label: '서버 관리', icon: <ServerIcon /> },
  { path: '/Cron', label: '크론 관리', icon: <ClockIcon /> },
];

const TopBar = ({
  onMenuClick,
  notificationCount = 3,
  drives = [],
  selectedRemote,
  onRemoteChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [remoteOpen, setRemoteOpen] = useState(false);
  const profileRef = useRef(null);
  const remoteRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (remoteRef.current && !remoteRef.current.contains(e.target)) {
        setRemoteOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const handlePickRemote = (drive) => {
    if (onRemoteChange) onRemoteChange(drive);
    setRemoteOpen(false);
  };

  const isActiveTab = (path) => {
    if (path === '/FileView') {
      return location.pathname === '/FileView' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Bar>
      <HamburgerButton onClick={onMenuClick} aria-label="menu">
        <MenuIcon />
      </HamburgerButton>

      <Brand to="/FileView">
        <BrandIcon><FolderSvg /></BrandIcon>
        <BrandText>
          <BrandName>AntsNest</BrandName>
          <BrandSub>파일서버 관리 시스템</BrandSub>
        </BrandText>
      </Brand>

      <RemoteSwitcher ref={remoteRef}>
        <RemoteButton
          type="button"
          onClick={() => setRemoteOpen((v) => !v)}
          disabled={drives.length === 0}
          aria-label="원격지 선택"
        >
          <RemoteDot empty={!selectedRemote ? 1 : 0} />
          <RemoteName>
            {selectedRemote ? selectedRemote.name : (drives.length === 0 ? '원격지 없음' : '원격지를 선택하세요')}
          </RemoteName>
          <RemoteCaret><ChevronDown /></RemoteCaret>
        </RemoteButton>
        <RemoteMenu open={remoteOpen}>
          {drives.length === 0 ? (
            <RemoteOption disabled>원격지 목록이 비어있습니다</RemoteOption>
          ) : (
            drives.map((drive) => {
              const isActive = selectedRemote?.name === drive.name;
              return (
                <RemoteOption
                  key={drive.name}
                  type="button"
                  active={isActive ? 1 : 0}
                  onClick={() => handlePickRemote(drive)}
                >
                  <RemoteOptionIcon active={isActive ? 1 : 0}>
                    <ServerSmallIcon />
                  </RemoteOptionIcon>
                  <RemoteOptionText>
                    <RemoteOptionName>{drive.name}</RemoteOptionName>
                    <RemoteOptionType>{drive.type}</RemoteOptionType>
                  </RemoteOptionText>
                  {isActive && <Check><CheckIcon /></Check>}
                </RemoteOption>
              );
            })
          )}
        </RemoteMenu>
      </RemoteSwitcher>

      <Tabs>
        {TABS.map((tab) => (
          <Tab key={tab.path} to={tab.path} active={isActiveTab(tab.path) ? 1 : 0}>
            {tab.icon}
            {tab.label}
          </Tab>
        ))}
      </Tabs>

      <Right>
        <IconButton aria-label="notifications">
          <BellIcon />
          {notificationCount > 0 && <Badge>{notificationCount}</Badge>}
        </IconButton>

        <ProfileWrap ref={profileRef}>
          <ProfileButton onClick={() => setProfileOpen((v) => !v)}>
            <Avatar><UserIconSvg /></Avatar>
            admin
            <ChevronDown />
          </ProfileButton>
          <ProfileMenu open={profileOpen}>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon /> 로그아웃
            </MenuItem>
          </ProfileMenu>
        </ProfileWrap>
      </Right>
    </Bar>
  );
};

export default TopBar;
